import { Field, FieldProps, FormikContextType, useFormikContext } from 'formik';
import React, { ReactElement, startTransition, useCallback, useEffect, useState } from 'react';
import ImageComponent from '@spa/components/image-component/image.component';
import classNames from 'clsx';
import styles from './file-input.component.module.scss';

export enum FileTypes {
  PNG = 'image/png',
  JPEG = 'image/jpeg',
  JPG = 'image/jpg',
  PDF = 'application/pdf'
}

export interface FileInputProps {
  readonly name: string;
  readonly label?: string | ReactElement;
  readonly files: Array<File>;
  readonly buttonText?: string;
  readonly limit: number;
  readonly accept: ReadonlyArray<string>;
  readonly errorMessage: string;
  readonly multiple?: boolean;
  readonly required?: boolean;
  readonly disabled?: boolean;
  readonly formError?: string;
  // Renders an "add" tile plus a thumbnail-preview grid (object-URL background images) instead
  // of the default button-and-file-name-list layout. Used where a visual preview matters more
  // than the file name/size (e.g. photo uploads).
  readonly thumbnails?: boolean;
  readonly addLabel?: string;
  readonly removeLabel?: string;
}

interface FilePreviewTileProps {
  readonly file: File;
  readonly removeLabel?: string;
  readonly disabled: boolean;
  readonly onRemove: () => void;
}

// The preview URL is (re)created in an effect keyed on `file`, not a lazy useState initializer.
// React's list `key` is a content signature (name-size-lastModified-occurrence), which two
// distinct File objects can share momentarily after a same-signature file is removed and the
// remaining one's occurrence index shifts -- React would then reuse this component instance for
// a different `file` prop. Tying creation to the `file` dependency (rather than "on mount only")
// means the preview is regenerated whenever the prop actually changes, even under that reuse.
function FilePreviewTileComponent({
  file,
  removeLabel,
  disabled,
  onRemove
}: FilePreviewTileProps): ReactElement {
  const [previewUrl, setPreviewUrl] = useState<string | undefined>(undefined);

  useEffect(() => {
    const url = URL.createObjectURL(file);

    // Wrapped in startTransition so setPreviewUrl isn't a bare, synchronous setState call at the
    // top level of the effect -- React Compiler can't optimize that shape either.
    startTransition(() => {
      setPreviewUrl(url);
    });

    return (): void => URL.revokeObjectURL(url);
  }, [file]);

  return (
    <button
      type="button"
      className={styles['file-input__tile']}
      onClick={onRemove}
      disabled={disabled}
      aria-label={removeLabel}
    >
      <span
        className={styles['file-input__photo']}
        style={previewUrl ? { backgroundImage: `url(${previewUrl})` } : undefined}
      />
      <span className={styles['file-input__remove-overlay']}>
        <ImageComponent src="/assets/icons/delete_bright.svg" alt="" width={48} height={48} />
      </span>
    </button>
  );
}

function FileInputComponent<T>({
  name,
  label,
  files,
  buttonText,
  limit,
  accept,
  errorMessage,
  multiple = false,
  required = false,
  disabled = false,
  formError,
  thumbnails = false,
  addLabel,
  removeLabel
}: FileInputProps): ReactElement {
  const form: FormikContextType<T> = useFormikContext<T>();
  const filesSize: number = files.reduce((acc, file) => acc + file.size, 0);
  const [error, setError] = useState<string | undefined>();

  const formatFileSize = (size: number): string => {
    let formattedSize: string;

    if (size < 1024) {
      formattedSize = `${size} B`;
    } else if (size < 1024 * 1024) {
      formattedSize = `${(size / 1024).toFixed(2)} KB`;
    } else {
      formattedSize = `${(size / (1024 * 1024)).toFixed(2)} MB`.replace(/\.00/, '');
    }

    return formattedSize;
  };

  const validateFiles = useCallback(
    (newFiles: Array<File>, existingFiles: Array<File>): Array<File> => {
      const validNewFiles: Array<File> = newFiles.filter((file) => accept.includes(file.type));
      let accumulatedSize: number = filesSize;
      let someFilesOverLimit: boolean = false;
      const filesToAdd: Array<File> = [];

      validNewFiles.forEach((file) => {
        if (accumulatedSize + file.size <= limit) {
          filesToAdd.push(file);
          accumulatedSize += file.size;
        } else {
          someFilesOverLimit = true;
        }
      });

      const validFiles: Array<File> = existingFiles.concat(filesToAdd);
      const someFilesRejectedByType = validNewFiles.length < newFiles.length;
      if (someFilesOverLimit || someFilesRejectedByType) {
        setError(errorMessage);
      } else {
        setError(undefined);
      }

      return validFiles;
    },
    [accept, errorMessage, filesSize, limit]
  );

  const handleRemoveFile = useCallback(
    (index: number): void => {
      const existingFiles: Array<File> = [...form.getFieldProps(name).value];
      existingFiles.splice(index, 1);
      setError(undefined);
      void form.setFieldValue(name, existingFiles);
    },
    [form, name]
  );
  const filesWithKeys: Array<{ readonly file: File; readonly key: string }> = [];
  const fileOccurrenceCountBySignature = new Map<string, number>();

  files.forEach((file) => {
    const fileSignature = `${file.name}-${file.size}-${file.lastModified}`;
    const occurrenceCount = fileOccurrenceCountBySignature.get(fileSignature) ?? 0;

    fileOccurrenceCountBySignature.set(fileSignature, occurrenceCount + 1);
    filesWithKeys.push({
      file,
      key: `${fileSignature}-${occurrenceCount}`
    });
  });

  const fileInput = (
    <Field name={name}>
      {({ form: fieldForm }: FieldProps) => (
        <input
          id={name}
          name={name}
          type="file"
          accept={accept.join(',')}
          multiple={multiple}
          onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
            const selectedFiles: Array<File> = event.currentTarget.files
              ? Array.from(event.currentTarget.files)
              : [];
            const existingFiles: Array<File> = fieldForm.values[name] || [];
            const validFiles: Array<File> = validateFiles(selectedFiles, existingFiles);
            void fieldForm.setFieldValue(name, validFiles);
            event.target.value = '';
          }}
          className={styles['file-input__input']}
          disabled={disabled}
        />
      )}
    </Field>
  );

  const errorBanner = (error || formError) && (
    <div className={classNames(styles['file-input__error'], styles['text-s'])}>
      <ImageComponent
        className={styles['file-input__error-icon']}
        src="/assets/icons/warning.svg"
        alt="warning-icon"
        width={20}
        height={20}
      />
      <span>{error}</span>
      <span className={'text-s'}>{formError}</span>
    </div>
  );

  if (thumbnails) {
    return (
      <div className={styles['file-input']}>
        <div className={styles['file-input__tiles']}>
          <label
            htmlFor={name}
            className={classNames(styles['file-input__tile'], styles['file-input__tile--add'], {
              [styles['file-input__tile--disabled']]: disabled
            })}
          >
            <span className={styles['file-input__add-square']}>{addLabel}</span>
          </label>
          {filesWithKeys.map(({ file, key }, index) => (
            <FilePreviewTileComponent
              key={key}
              file={file}
              onRemove={(): void => handleRemoveFile(index)}
              removeLabel={removeLabel}
              disabled={disabled}
            />
          ))}
        </div>
        {fileInput}
        {errorBanner}
      </div>
    );
  }

  return (
    <div className={styles['file-input']}>
      <div className={styles['file-input__input-field']}>
        <label className={styles['file-input__input-label']} htmlFor={name}>
          {label}
          {required && <span> *</span>}
        </label>
        {fileInput}
        <div className={styles['file-input__overlay']}>
          <label htmlFor={name} className={classNames(styles['file-input__button'], 'body-bold')}>
            {buttonText}
            <ImageComponent
              src="/assets/icons/upload-icon.svg"
              alt="Upload"
              width={20}
              height={20}
            />
          </label>
          {filesSize > 0 && (
            <span className={classNames(styles['fileInput__total-size'], 'text-s-immutable')}>
              {`${formatFileSize(filesSize)}/${formatFileSize(limit)}`}
            </span>
          )}
        </div>
      </div>
      {errorBanner}
      {filesWithKeys.length > 0 && (
        <div className={styles['file-input__file-list']}>
          {filesWithKeys.map(({ file, key }, index) => (
            <div key={key} className={styles['file-input__file']}>
              <div className={styles['file-input__file-info']}>
                <span>{file.name}</span>
                <span className={styles['file-input__file-size']}>{formatFileSize(file.size)}</span>
              </div>
              <button
                type="button"
                onClick={() => handleRemoveFile(index)}
                className={styles['file-input__remove-button']}
              >
                <ImageComponent
                  src="/assets/icons/close-icon.svg"
                  alt="Remove"
                  width={20}
                  height={20}
                />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default FileInputComponent;
